import { BackendKeywordTag } from '../../constants/tags'

export const getLocalizedName = (keyword: BackendKeywordTag, language: string): string => {
  const hungarian = (keyword.Name || '').trim()
  const english = (keyword.EN_Name || '').trim()

  if (language.startsWith('en')) {
    return english || hungarian
  }

  return hungarian || english
}

export const getFirstHungarianLetter = (text: string): string => {
  const upperText = text.toLocaleUpperCase('hu-HU')

  // Hungarian digraphs and trigraphs (order matters - check longer ones first)
  const hungarianLetters = ['DZS', 'CS', 'GY', 'LY', 'NY', 'SZ', 'TY', 'ZS', 'DZ']

  for (const letter of hungarianLetters) {
    if (upperText.startsWith(letter)) {
      return letter
    }
  }

  // If no digraph/trigraph, return the first character
  const firstChar = upperText.charAt(0)
  return /^[A-ZÁÉÍÓÖŐÚÜŰ]$/.test(firstChar) ? firstChar : '#'
}

export const getFirstLetter = (text: string, language: string): string => {
  if (language.startsWith('hu')) {
    return getFirstHungarianLetter(text)
  }

  // English has no digraph letters and no accented initials, so group by the plain base letter
  const firstChar = text.normalize('NFD').replace(/[̀-ͯ]/g, '').charAt(0).toLocaleUpperCase('en')

  return /^[A-Z]$/.test(firstChar) ? firstChar : '#'
}

export const getKeywordSizeLevel = (count: number, minCount: number, maxCount: number): number => {
  if (maxCount === minCount) return 0
  if (count <= 4) return -1
  if (count <= 19) return 0
  if (count <= 49) return 1
  if (count <= 99) return 2
  if (count <= 199) return 3
  return 4
}

// Higher value = more dramatic size difference between small and large tags
export const TAG_SIZE_SCALE = 0.2

export const keywordButtonFontSize = (level: number): string => {
  return `${1 + level * TAG_SIZE_SCALE}rem`
}

export const keywordButtonPadding = (level: number): string => {
  if (level <= -2) return '2px 6px'
  if (level === -1) return '3px 7px'
  const paddingScale = 1 + level * 0.5
  return `${4 + level * paddingScale}px ${8 + level * paddingScale * 2}px`
}

// Up to this many tags a column is a single vertical stack, above it the tags wrap into a cloud
export const STACKED_COLUMN_MAX_ITEMS = 4

export interface CloudTag {
  width: number
  height: number
  // the motif count of the keyword: the heavier a tag, the closer it sits to the centre of the cloud
  weight: number
}

// Width to height ratio a tag cloud is laid out to when the available height allows it
const CLOUD_ASPECT_RATIO = 1.8
// A last row narrower than this share of the cloud reads as a leftover tag
const ORPHAN_ROW_RATIO = 0.3
const ORPHAN_ROW_PENALTY = 0.25
const WIDTH_STEP = 4
const MAX_CENTERING_PASSES = 8

// Orders the indexes so the heaviest one is in the middle and the weights fall off towards both ends.
// Equal weights keep their (alphabetical) order.
export const centerOut = (indexes: number[], tags: CloudTag[]): number[] => {
  const heaviestFirst = [...indexes].sort((a, b) => tags[b].weight - tags[a].weight || a - b)
  const left: number[] = []
  const right: number[] = []

  heaviestFirst.forEach((index, position) => {
    if (position % 2 === 0) left.unshift(index)
    else right.push(index)
  })

  return [...left, ...right]
}

// Mirrors how the browser wraps the tags (flex-wrap: wrap) into a container of the given width
const wrapTags = (
  order: number[],
  tags: CloudTag[],
  containerWidth: number,
  columnGap: number,
  rowGap: number,
) => {
  const rows: number[][] = [[]]
  let width = 0
  let height = 0
  let rowWidth = 0
  let rowHeight = 0

  order.forEach(index => {
    const tag = tags[index]
    if (rowWidth > 0 && rowWidth + columnGap + tag.width > containerWidth) {
      width = Math.max(width, rowWidth)
      height += rowHeight + rowGap
      rowWidth = 0
      rowHeight = 0
      rows.push([])
    }
    rowWidth += (rowWidth > 0 ? columnGap : 0) + tag.width
    rowHeight = Math.max(rowHeight, tag.height)
    rows[rows.length - 1].push(index)
  })

  return {
    rows,
    width: Math.max(width, rowWidth),
    height: height + rowHeight,
    lastRowWidth: rowWidth,
  }
}

// Moves the heaviest tag of every row to the middle of its row. A reordered row can leave room for
// the (now different) first tag of the next row, which shifts the rows, so it is repeated until the
// rows settle. Returns the order (indexes into `tags`) and the height the browser will lay it out to.
const centerRows = (
  sequence: number[],
  tags: CloudTag[],
  containerWidth: number,
  columnGap: number,
  rowGap: number,
) => {
  let order = sequence
  let cloud = wrapTags(order, tags, containerWidth, columnGap, rowGap)

  for (let pass = 0; pass < MAX_CENTERING_PASSES; pass++) {
    const rowLengths = cloud.rows.map(row => row.length)
    order = cloud.rows.flatMap(row => centerOut(row, tags))
    cloud = wrapTags(order, tags, containerWidth, columnGap, rowGap)

    if (cloud.rows.map(row => row.length).join() === rowLengths.join()) break
  }

  return { order, height: cloud.height }
}

// CSS can't size a wrapping container by its height, so the width is searched for here: the cloud
// that fits the available height and is the closest to the preferred aspect ratio.
// Returns that width and the order of the tags (indexes into `tags`) that puts the heaviest tags
// in the centre of the cloud: in the middle rows, and in the middle of each row.
export const getCloudLayout = (
  tags: CloudTag[],
  columnGap: number,
  rowGap: number,
  maxHeight: number,
): { width: number; order: number[] } => {
  // the heaviest tags in the middle of the sequence end up in the middle rows once wrapped
  const sequence = centerOut(
    tags.map((_, index) => index),
    tags,
  )
  const minWidth = Math.max(...tags.map(tag => tag.width))
  const maxWidth = tags.reduce((sum, tag) => sum + tag.width, columnGap * (tags.length - 1))

  let width = maxWidth
  let bestScore = Number.POSITIVE_INFINITY

  for (let candidate = minWidth; candidate < maxWidth + WIDTH_STEP; candidate += WIDTH_STEP) {
    const cloud = wrapTags(sequence, tags, candidate, columnGap, rowGap)
    if (cloud.height > maxHeight && cloud.width < maxWidth) continue

    let score = Math.abs(Math.log(cloud.width / cloud.height / CLOUD_ASPECT_RATIO))
    if (cloud.rows.length > 1 && cloud.lastRowWidth < cloud.width * ORPHAN_ROW_RATIO) {
      score += ORPHAN_ROW_PENALTY
    }

    if (score < bestScore) {
      bestScore = score
      width = cloud.width
    }
  }

  let centered = centerRows(sequence, tags, width, columnGap, rowGap)
  // the shifted rows are hardly ever taller than the searched ones, the cloud is widened if they are
  while (centered.height > maxHeight && width < maxWidth) {
    width += WIDTH_STEP
    centered = centerRows(sequence, tags, width, columnGap, rowGap)
  }

  return { width, order: centered.order }
}
